import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Table, Spinner, Button, Input, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { toast } from "react-toastify";
import Pagination from "../../Components/Paginate";
import { FaTicketAlt, FaEye, FaDownload, FaCheck, FaTimes, FaFilter, FaCalendarAlt, FaMapMarkerAlt, FaCheckCircle, FaEllipsisV } from "react-icons/fa";
import { fetchTickets, fetchSpots, updateTicketStatus } from "../../redux/actions/TicketActions";

const CounterTickets = () => {
  const dispatch = useDispatch();
  const {
    TicketReducer: { tickets, count: ticketsCount, spots, loading },
  } = useSelector((state) => state);

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("desc");
  const [paginate, setPaginate] = useState(10);
  const [status, setStatus] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [place, setPlace] = useState("all");
  const [partnerType, setPartnerType] = useState("all");
  const [quantityType, setQuantityType] = useState("all");
  const [bookingStatus, setBookingStatus] = useState("all");
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [actionDropdowns, setActionDropdowns] = useState({});

  useEffect(() => {
    const params = {
      page: page + 1,
      limit: paginate,
      search,
      sort
    };

    if (status !== "all") {
      if (status === "active") params.is_valid = 1;
      else if (status === "used") params.is_used = 1;
      else if (status === "expired") params.is_valid = 0;
    }

    if (place !== "all") params.place = place;
    if (partnerType !== "all") params.partner_type = partnerType;
    if (quantityType !== "all") params.quantity_type = quantityType;
    if (bookingStatus !== "all") params.booking_status = bookingStatus;

    if (dateRange !== "all") {
      if (dateRange === "today") {
        const today = new Date().toISOString().split('T')[0];
        params.start_date = today;
        params.end_date = today;
      } else if (dateRange === "week") {
        const today = new Date();
        const weekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
        params.start_date = weekStart.toISOString().split('T')[0];
        params.end_date = new Date().toISOString().split('T')[0];
      } else if (dateRange === "month") {
        const today = new Date();
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        params.start_date = monthStart.toISOString().split('T')[0];
        params.end_date = new Date().toISOString().split('T')[0];
      } else if (dateRange === "custom" && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
    }

    dispatch(fetchTickets(params, 'counter'));
  }, [dispatch, paginate, page, search, sort, status, dateRange, place, customStartDate, customEndDate]);

  useEffect(() => {
    dispatch(fetchSpots());
  }, [dispatch]);



  const clearFilters = () => {
    setSearch("");
    setStatus("all");
    setDateRange("all");
    setPlace("all");
    setPartnerType("all");
    setQuantityType("all");
    setBookingStatus("all");
    setShowCustomRange(false);
    setCustomStartDate("");
    setCustomEndDate("");
  };

  const handleExportCSV = () => {
    if (!tickets?.length) return;
    const csvData = tickets.map((ticket, index) => ({
      "SL. No.": index + 1,
      "Ticket ID": ticket.ticket_id,
      "Booking ID": ticket.booking_id || 'N/A',
      "Place Name": ticket.place?.place_name,
      "Visitor Name": ticket.visitor_name,
      "Mobile": ticket.visitor_phone || ticket.mobile,
      "Booking Date": new Date(ticket.createdAt).toLocaleDateString(),
      "Visit Date": new Date(ticket.booking_date).toLocaleDateString(),
      "Payment Mode": ticket.payment_mode,
      "Amount": `₹${ticket.total_amount}`,
      "Status": ticket.is_used === 1 ? 'Used' : ticket.is_valid === 1 ? 'Active' : 'Expired'
    }));
    
    const csv = [Object.keys(csvData[0]).join(','), ...csvData.map(row => Object.values(row).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'counter-tickets.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    toast.info("PDF export functionality will be implemented soon");
  };

  const handleMarkAsUsed = (ticketId) => {
    dispatch(updateTicketStatus(ticketId, 'used'));
  };

  const handleCancelTicket = (ticketId) => {
    if (window.confirm("Are you sure you want to cancel this ticket?")) {
      dispatch(updateTicketStatus(ticketId, 'cancelled'));
    }
  };

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-12">
          <div className="mb-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 text-white fs-4">
              <FaTicketAlt className="me-2" />
              Counter Ticket Management
            </h5>
          </div>
        </div>
      </div>

      <div
        className="bg-white px-3 py-4 rounded-2 my-4"
        style={{ boxShadow: "4px 4px 18px rgba(0,0,0,0.15)" }}
      >
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div className="flex-grow-1" style={{ maxWidth: "190px" }}>
            <Input
              type="text"
              className="form-control"
              placeholder="🔍 Search by ID, Visitor, Phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="d-flex flex-wrap gap-2">
            <div className="position-relative">
              <FaCheckCircle className="position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: '#6c757d' }} />
              <Input
                type="select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{ width: "140px", paddingLeft: '30px' }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="used">Used</option>
                <option value="expired">Expired</option>
              </Input>
            </div>
            <div className="position-relative">
              <FaMapMarkerAlt className="position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: '#6c757d' }} />
              <Input
                type="select"
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                style={{ width: "140px", paddingLeft: '30px' }}
              >
                <option value="all">All Spots</option>
                {spots.map((spot) => (
                  <option key={spot} value={spot}>
                    {spot}
                  </option>
                ))}
              </Input>
            </div>
            <div className="position-relative">
              <FaCalendarAlt className="position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: '#6c757d' }} />
              <Input
                type="select"
                value={dateRange}
                onChange={(e) => {
                  setDateRange(e.target.value);
                  setShowCustomRange(e.target.value === "custom");
                }}
                style={{ width: "140px", paddingLeft: '30px' }}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="custom">Custom Range</option>
              </Input>
            </div>
            <Input
              type="select"
              value={partnerType}
              onChange={(e) => setPartnerType(e.target.value)}
              style={{ width: "140px" }}
            >
              <option value="all">All Partners</option>
              <option value="direct">Direct</option>
              <option value="agent">Agent</option>
              <option value="reseller">Reseller</option>
            </Input>
            <Input
              type="select"
              value={quantityType}
              onChange={(e) => setQuantityType(e.target.value)}
              style={{ width: "140px" }}
            >
              <option value="all">All Quantity</option>
              <option value="single">Single</option>
              <option value="group">Group</option>
              <option value="family">Family</option>
            </Input>
            <Input
              type="select"
              value={bookingStatus}
              onChange={(e) => setBookingStatus(e.target.value)}
              style={{ width: "140px" }}
            >
              <option value="all">All Bookings</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </Input>
            <Dropdown
              isOpen={exportDropdownOpen}
              toggle={() => setExportDropdownOpen(!exportDropdownOpen)}
            >
              <DropdownToggle className="btn btn-outline-secondary text-white">
                <FaDownload className="me-1" />
                Export
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={handleExportCSV}>
                  Export CSV
                </DropdownItem>
                <DropdownItem onClick={handleExportPDF}>
                  Export PDF
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
            <button
              className="btn btn-outline-danger"
              onClick={clearFilters}
            >
              <FaFilter className="me-1" />
              Clear Filters
            </button>
          </div>
        </div>
        {showCustomRange && (
          <div className="d-flex align-items-center gap-2 mb-3 p-3 bg-light rounded">
            <label className="fw-bold mb-0">From:</label>
            <Input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              style={{ width: "150px" }}
            />
            <label className="fw-bold mb-0">To:</label>
            <Input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              style={{ width: "150px" }}
            />
          </div>
        )}
        <div className="row">
          <div className="col-md-12">
            <Table hover responsive>
              <thead>
                <tr>
                  <th>SL. No.</th>
                  <th>Ticket ID</th>
                  <th>Booking ID</th>
                  <th>Place Name</th>
                  <th>Visitor Name</th>
                  <th>Mobile</th>
                  <th>Partner Type</th>
                  <th>Quantity Type</th>
                  <th>Booking Date</th>
                  <th>Visit Date</th>
                  <th>Payment Mode</th>
                  <th>Amount</th>
                  <th>Booking Status</th>
                  <th>Ticket Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="15" className="text-center">
                      <Spinner />
                    </td>
                  </tr>
                ) : tickets.length === 0 ? (
                  <tr>
                    <td colSpan="15" className="text-center">No data found</td>
                  </tr>
                ) : (
                  tickets.map((ticket, index) => (
                    <tr key={ticket.id}>
                      <th scope="row">{index + 1 + page * paginate}</th>
                      <td>{ticket.ticket_id}</td>
                      <td>{ticket.booking_id || 'N/A'}</td>
                      <td>{ticket.place?.place_name}</td>
                      <td>{ticket.visitor_name}</td>
                      <td>{ticket.visitor_phone || ticket.mobile}</td>
                      <td>{ticket.partner_type || 'Direct'}</td>
                      <td>{ticket.quantity_type || 'Single'}</td>
                      <td>{new Date(ticket.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} {new Date(ticket.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</td>
                      <td>{new Date(ticket.booking_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      <td>{ticket.payment_mode}</td>
                      <td>₹{ticket.total_amount}</td>
                      <td>
                        <span className={`badge ${ticket.booking_status === 'confirmed' ? 'bg-success' : ticket.booking_status === 'pending' ? 'bg-warning' : 'bg-danger'}`}>
                          {ticket.booking_status || 'Confirmed'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${
                          ticket.is_used === 1 ? 'bg-warning' : 
                          ticket.is_valid === 1 ? 'bg-success' : 'bg-secondary'
                        }`}>
                          {ticket.is_used === 1 ? 'Used' : ticket.is_valid === 1 ? 'Active' : 'Expired'}
                        </span>
                      </td>
                      <td>
                        <Dropdown
                          isOpen={actionDropdowns[ticket.id] || false}
                          toggle={() => setActionDropdowns(prev => ({ ...prev, [ticket.id]: !prev[ticket.id] }))}
                        >
                          <DropdownToggle className="btn btn-sm btn-outline-secondary">
                            <FaEllipsisV style={{ color: 'white' }} />
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem>
                              <FaEye className="me-2" /> View
                            </DropdownItem>
                            <DropdownItem>
                              <FaDownload className="me-2" /> Download
                            </DropdownItem>
                            <DropdownItem 
                              onClick={() => handleMarkAsUsed(ticket.id)}
                              disabled={ticket.is_used === 1}
                            >
                              <FaCheck className="me-2" /> Mark as Used
                            </DropdownItem>
                            <DropdownItem 
                              onClick={() => handleCancelTicket(ticket.id)}
                              disabled={ticket.is_used === 1}
                            >
                              <FaTimes className="me-2" /> Cancel
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
        <div className="row align-items-center">
          <div className="col-md-6 d-flex">
            <div className="col-md-2">
              <Input
                type="select"
                id="paginate"
                onChange={(e) => setPaginate(e.target.value)}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Input>
            </div>
          </div>
          <div className="col-md-6">
            <Pagination
              totalItems={ticketsCount}
              itemsPerPage={paginate}
              currentPage={page}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CounterTickets;